import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { bookingService } from '../../services/bookingService';
import { universityService } from '../../services/universityService';
import { Booking, University, StudentTripStats } from '../../types';
import { useRouter, Link } from '../../router/Router';
import {
  Ticket,
  Calendar,
  Clock,
  MapPin,
  Bus,
  ArrowLeft,
  ShieldCheck,
  PlusCircle,
  History,
  Send,
  User,
  Sparkles,
  Search,
  AlertTriangle,
  CreditCard,
  Building2,
  CheckCircle,
  Copy,
  Check,
  Share2,
  Compass,
  Zap,
  Phone,
} from 'lucide-react';

import { DemandDataEntryModal } from '../admin/demand/DemandDataEntryModal';
import { StudentPromoCard } from './StudentPromoCard';
import './StudentDashboard.css';

interface CountdownState {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
  isToday: boolean;
}

export const StudentDashboard: React.FC = () => {
  const { user, studentProfile } = useAuth();
  const { navigate } = useRouter();

  // Core Data States
  const [upcomingBooking, setUpcomingBooking] = useState<Booking | null>(null);
  const [pendingBooking, setPendingBooking] = useState<Booking | null>(null);
  const [universities, setUniversities] = useState<University[]>([]);
  const [activeBookingsCount, setActiveBookingsCount] = useState<number>(0);
  const [tripStats, setTripStats] = useState<StudentTripStats | null>(null);

  // Filter & Search States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('ALL');
  const [isLoading, setIsLoading] = useState(true);

  // Modal & Interaction States
  const [isDemandModalOpen, setIsDemandModalOpen] = useState(false);
  const [demandSuccessNotice, setDemandSuccessNotice] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [shareSuccessNotice, setShareSuccessNotice] = useState<boolean>(false);

  // 1. Fetch Student Bookings & Universities
  useEffect(() => {
    let isMounted = true;
    const loadDashboard = async () => {
      setIsLoading(true);
      try {
        // Fetch universities catalog
        try {
          const unis = await universityService.getActiveUniversities();
          if (isMounted) setUniversities(unis);
        } catch (uErr) {
          console.warn('Error loading universities in dashboard:', uErr);
        }

        // Fetch bookings if user is logged in
        if (user?.uid) {
          try {
            const [upcoming, pending, allStudentBookings, stats] = await Promise.all([
              bookingService.getUpcomingBooking(user.uid),
              bookingService.getPendingVerificationBooking(user.uid),
              bookingService.getStudentBookings(user.uid),
              bookingService.getStudentTripStats(user.uid),
            ]);

            if (isMounted) {
              setUpcomingBooking(upcoming);
              setTripStats(stats);
              if (pending && upcoming?.id !== pending.id) {
                setPendingBooking(pending);
              } else if (pending && !upcoming) {
                setPendingBooking(pending);
              } else {
                setPendingBooking(null);
              }

              const confirmedCount = allStudentBookings.filter(
                (b) => b.status === 'CONFIRMED' || b.paymentStatus === 'PAID'
              ).length;
              setActiveBookingsCount(confirmedCount);
            }
          } catch (bErr) {
            console.warn('Could not load student bookings:', bErr);
          }
        }
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadDashboard();
    return () => {
      isMounted = false;
    };
  }, [user?.uid]);

  // 2. Greeting based on current time of day
  const timeBasedGreeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour >= 4 && hour < 12) return { text: 'صباح الخير', emoji: '☀️' };
    if (hour >= 12 && hour < 17) return { text: 'طاب يومك', emoji: '🌤️' };
    return { text: 'مساء الخير', emoji: '🌙' };
  }, []);

  // 3. Live Countdown Timer Calculation for Upcoming Trip
  const [countdown, setCountdown] = useState<CountdownState>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isPast: false,
    isToday: false,
  });

  useEffect(() => {
    if (!upcomingBooking?.tripDate) return;

    const updateTimer = () => {
      try {
        const dateParts = upcomingBooking.tripDate.split('-');
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);

        let hour = 6;
        let minute = 30;
        if (upcomingBooking.departureTime) {
          const cleanTime = upcomingBooking.departureTime.replace(/[^0-9:]/g, '');
          const [hStr, mStr] = cleanTime.split(':');
          if (hStr) hour = parseInt(hStr, 10);
          if (mStr) minute = parseInt(mStr, 10);
        }

        const departureDate = new Date(year, month, day, hour, minute, 0);
        const now = new Date();
        const diffMs = departureDate.getTime() - now.getTime();

        if (diffMs <= 0) {
          setCountdown({
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            isPast: true,
            isToday: true,
          });
          return;
        }

        const totalSeconds = Math.floor(diffMs / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const isToday = days === 0;

        setCountdown({ days, hours, minutes, seconds, isPast: false, isToday });
      } catch (err) {
        console.warn('Countdown calculation error:', err);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [upcomingBooking?.tripDate, upcomingBooking?.departureTime]);

  // 4. Regional Categorization Logic
  const REGIONAL_CATEGORIES: { id: string; label: string; count?: number }[] = [
    { id: 'ALL', label: 'جميع الجامعات' },
    { id: 'DELTA', label: '🌾 جامعات الدلتا' },
    { id: 'CAIRO', label: '🏛️ القاهرة والمدن الجديدة' },
    { id: 'COAST', label: '🌊 الساحل ومطروح' },
  ];

  const matchesRegion = useCallback((uni: University, region: string): boolean => {
    if (region === 'ALL') return true;
    const g = uni.governorate || '';
    const name = uni.name || '';

    if (region === 'DELTA') {
      return (
        g.includes('الغربية') ||
        g.includes('طنطا') ||
        g.includes('كفر الشيخ') ||
        g.includes('الدقهلية') ||
        g.includes('المنصورة') ||
        g.includes('المنوفية') ||
        g.includes('الشرقية') ||
        g.includes('دمياط') ||
        name.includes('طنطا') ||
        name.includes('كفر الشيخ') ||
        name.includes('المنصورة') ||
        name.includes('الدلتا')
      );
    }
    if (region === 'CAIRO') {
      return (
        g.includes('القاهرة') ||
        g.includes('الجيزة') ||
        g.includes('حلوان') ||
        g.includes('بدر') ||
        g.includes('الشروق') ||
        name.includes('القاهرة') ||
        name.includes('حلوان') ||
        name.includes('عين شمس') ||
        name.includes('بدر') ||
        name.includes('الشروق') ||
        name.includes('الروسية') ||
        name.includes('الألمانية')
      );
    }
    if (region === 'COAST') {
      return (
        g.includes('مطروح') ||
        g.includes('العلمين') ||
        g.includes('برج العرب') ||
        name.includes('مطروح') ||
        name.includes('العلمين') ||
        name.includes('برج العرب')
      );
    }
    return true;
  }, []);

  // Filtered universities based on search & region
  const filteredUniversities = useMemo(() => {
    return universities.filter((u) => {
      const matchReg = matchesRegion(u, selectedRegion);
      const matchSearch =
        searchQuery.trim() === '' ||
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.campus && u.campus.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (u.governorate && u.governorate.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchReg && matchSearch;
    });
  }, [universities, searchQuery, selectedRegion, matchesRegion]);

  const popularUnis = useMemo(() => {
    return universities.slice(0, 5);
  }, [universities]);

  // Copy code helper
  const handleCopyBookingCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2500);
  };

  // Share Ticket with Parent via WhatsApp
  const handleShareTicketWithParent = (booking: Booking) => {
    const parentMsg = `السلام عليكم،
تفاصيل تذكرة السفر المؤكدة مع OTO STUDENTS 🚌🎓:
• الطالب: ${studentProfile?.fullName || 'الطالب'}
• الجامعة: ${booking.universityName}
• تاريخ الرحلة: ${booking.tripDate}
• موعد التحرك: ${booking.departureTime} صباحاً
• محطة الانطلاق: ${booking.origin || 'محطة سيدي جابر'}
• رقم المقعد: #${booking.seatNumber}
• كود الحجز: ${booking.bookingCode}
• الحالة: مدفوع ومؤكد رسمياً ✅`;

    const encoded = encodeURIComponent(parentMsg);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
    setShareSuccessNotice(true);
    setTimeout(() => setShareSuccessNotice(false), 3500);
  };

  return (
    <div className="student-home-container" dir="rtl">
      {/* ====================================================================
          1. Smart Hero Greeting & Identity Header
          ==================================================================== */}
      <section className="student-hero-header-card">
        <div className="hero-header-top-row">
          <div className="hero-student-meta-block">
            <div className="hero-avatar-wrapper">
              <div className="hero-avatar-circle">
                {studentProfile?.fullName ? studentProfile.fullName.charAt(0) : 'ط'}
              </div>
              <span className="hero-avatar-status-dot" title="حساب نشط ومسجل"></span>
            </div>

            <div className="hero-text-details">
              <span className="hero-salutation-pill">
                <Sparkles size={13} />
                <span>{timeBasedGreeting.text} {timeBasedGreeting.emoji}</span>
              </span>
              <h1 className="hero-student-name">
                أهلاً بك، {studentProfile?.fullName || 'عزيزي الطالب'}
              </h1>
              <p className="hero-student-sub">
                مقيد بـ <strong>{studentProfile?.universityName || 'الجامعة'}</strong>
                <span>• {studentProfile?.universityGovernorate || 'مصر'}</span>
                {studentProfile?.assignedRoute && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginRight: '8px',
                      backgroundColor: '#fff1f2',
                      color: '#e5232a',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      border: '1px solid #fecdd3',
                    }}
                  >
                    <Bus size={12} />
                    خط السير المعتمد: {studentProfile.assignedRoute}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Quick Floating Stat Pills */}
          <div className="hero-header-stat-pills">
            <div className="hero-stat-pill verified" title="حسابك معتمد وموثق بنظام OTO">
              <ShieldCheck size={16} />
              <span>حساب موثق</span>
            </div>

            <div className="hero-stat-pill active-trips" title="إجمالي الرحلات المحجوزة">
              <Ticket size={15} />
              <span>{tripStats ? tripStats.totalBooked : activeBookingsCount} رحلات محجوزة</span>
            </div>

            {tripStats && tripStats.remaining > 0 && (
              <div
                className="hero-stat-pill"
                style={{ backgroundColor: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' }}
                title="الرحلات القادمة المتبقية في اشتراكك"
              >
                <Clock size={15} />
                <span>{tripStats.remaining} رحلات متبقية</span>
              </div>
            )}

            <div className="hero-stat-pill rewards">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span>سفير OTO المميز</span>
            </div>
          </div>
        </div>

        {/* Live Travel & Road Advisory Bar */}
        <div className="hero-advisory-bar">
          <div className="advisory-text">
            <span className="advisory-pulse"></span>
            <span>
              <strong>تحديث تشغيلي مباشر:</strong> انطلاق الرحلات من المحطات المعتمدة (الموقف الجديد بمحرم بيك، الكيلو 21) بمواعيدها المحددة بانسيابية تامة.
            </span>
          </div>

          <Link to="/student/universities" className="advisory-quick-link">
            <span>استعراض جدول المواعيد</span>
            <ArrowLeft size={14} />
          </Link>
        </div>
      </section>

      {/* ====================================================================
          1.5 Referral & Ambassador Reward Card
          ==================================================================== */}
      <StudentPromoCard studentProfile={studentProfile} />

      {/* ====================================================================
          1.8 Multi-Trip Subscription Tracker Card (للمشتركين في عدة رحلات)
          ==================================================================== */}
      {tripStats && tripStats.isMultiTripSubscriber && (
        <section
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #1e293b 100%)',
            color: '#ffffff',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.25)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            direction: 'rtl',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '16px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              paddingBottom: '14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                  border: '1px solid rgba(96, 165, 250, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#93c5fd',
                }}
              >
                <Ticket size={22} />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#ffffff', margin: 0 }}>
                    اشتراكك النشط في عدة رحلات
                  </h3>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(59, 130, 246, 0.3)',
                      color: '#bfdbfe',
                      padding: '2px 10px',
                      borderRadius: '999px',
                      border: '1px solid rgba(96, 165, 250, 0.4)',
                    }}
                  >
                    باقة ({tripStats.totalBooked} رحلات)
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#cbd5e1', margin: '4px 0 0 0' }}>
                  خط سيرك المعتمد:{' '}
                  <strong style={{ color: '#fca5a5' }}>
                    {studentProfile?.assignedRoute || `الموقف الجديد بمحرم بيك ◄ ${studentProfile?.universityName || 'الجامعة'}`}
                  </strong>
                </p>
              </div>
            </div>

            <Link
              to="/student/bookings"
              style={{
                fontSize: '12px',
                backgroundColor: '#ffffff',
                color: '#1e3a8a',
                fontWeight: 'bold',
                padding: '8px 14px',
                borderRadius: '10px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              }}
            >
              <span>مواعيد وتفاصيل الرحلات</span>
              <ArrowLeft size={14} />
            </Link>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              textAlign: 'center',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                إجمالي الرحلات المحجوزة
              </span>
              <strong style={{ fontSize: '22px', fontFamily: 'monospace', color: '#ffffff' }}>
                {tripStats.totalBooked}
              </strong>
              <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px' }}>رحلة</span>
            </div>

            <div
              style={{
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid rgba(52, 211, 153, 0.3)',
              }}
            >
              <span style={{ fontSize: '11px', color: '#6ee7b7', display: 'block', marginBottom: '4px' }}>
                الرحلات المتبقية القادمة
              </span>
              <strong style={{ fontSize: '22px', fontFamily: 'monospace', color: '#34d399' }}>
                {tripStats.remaining}
              </strong>
              <span style={{ fontSize: '11px', color: '#a7f3d0', display: 'block', marginTop: '2px' }}>رحلة متاحة</span>
            </div>

            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.07)',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                الرحلات المستهلكة
              </span>
              <strong style={{ fontSize: '22px', fontFamily: 'monospace', color: '#cbd5e1' }}>
                {tripStats.completed}
              </strong>
              <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px' }}>تم السفر</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: '#94a3b8',
                marginBottom: '6px',
              }}
            >
              <span>نسبة استهلاك رحلات الاشتراك:</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#38bdf8' }}>
                {Math.round((tripStats.completed / (tripStats.totalBooked || 1)) * 100)}% مكتمل
              </span>
            </div>
            <div
              style={{
                width: '100%',
                backgroundColor: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '999px',
                height: '8px',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(90deg, #3b82f6 0%, #10b981 100%)',
                  height: '100%',
                  borderRadius: '999px',
                  width: `${Math.min(100, Math.round((tripStats.completed / (tripStats.totalBooked || 1)) * 100))}%`,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* ====================================================================
          2. Pending Payment Verification Timeline Tracker (If Active)
          ==================================================================== */}
      {pendingBooking && (
        <section
          className={`payment-status-tracker-card ${
            pendingBooking.paymentStatus === 'REJECTED'
              ? 'rejected'
              : pendingBooking.paymentMethod === 'BRANCH'
              ? 'branch'
              : 'pending'
          }`}
        >
          <div className="tracker-header-row">
            <div className="tracker-title-group">
              <div
                className={`tracker-icon-box ${
                  pendingBooking.paymentStatus === 'REJECTED'
                    ? 'rejected'
                    : pendingBooking.paymentMethod === 'BRANCH'
                    ? 'branch'
                    : 'pending'
                }`}
              >
                {pendingBooking.paymentStatus === 'REJECTED' ? (
                  <AlertTriangle size={26} />
                ) : pendingBooking.paymentMethod === 'BRANCH' ? (
                  <Building2 size={26} />
                ) : (
                  <Clock size={26} className="animate-pulse" />
                )}
              </div>

              <div className="tracker-title-text">
                <h3>
                  {pendingBooking.paymentStatus === 'REJECTED'
                    ? 'تعذر اعتماد إثبات السداد — يرجى إعادة الإرسال'
                    : pendingBooking.paymentMethod === 'BRANCH'
                    ? 'طلبك مسجل — بانتظار السداد من مقر فرع OTO'
                    : 'طلبك قيد مراجعة وتدقيق الإدارة الآن ⏳'}
                </h3>
                <p>
                  {pendingBooking.paymentStatus === 'REJECTED'
                    ? 'يرجى رفع إيصال تحويل واضح بالبيانات الصحيحة لتثبيت مقعدك فوراً.'
                    : pendingBooking.paymentMethod === 'BRANCH'
                    ? 'يرجى التوجه لمقر فرع OTO لتأكيد التذكرة وطباعة الكود قبل موعد الرحلة.'
                    : 'نقوم بمطابقة رقم التحويل المالي وسوف تصدر تذكرتك الرقمية فور اعتمادها (خلال 15 دقيقة).'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="pass-code-pill" style={{ color: '#0369A1', background: '#E0F2FE' }}>
                كود الحجز: {pendingBooking.bookingCode}
              </span>
            </div>
          </div>

          {/* 3-Step Interactive Timeline */}
          <div className="tracker-timeline-bar">
            <div className="timeline-track-line">
              <div
                className="timeline-track-progress"
                style={{
                  width:
                    pendingBooking.paymentStatus === 'REJECTED'
                      ? '35%'
                      : pendingBooking.paymentStatus === 'PAID'
                      ? '100%'
                      : '50%',
                }}
              ></div>
            </div>

            {/* Step 1 */}
            <div className="tracker-step completed">
              <div className="step-indicator-circle">
                <Check size={18} />
              </div>
              <span className="step-title">تم تقديم الطلب</span>
              <span className="step-sub">مسجل بالنظام</span>
            </div>

            {/* Step 2 */}
            <div
              className={`tracker-step ${
                pendingBooking.paymentStatus === 'REJECTED' ? 'rejected' : 'active'
              }`}
            >
              <div className="step-indicator-circle">
                {pendingBooking.paymentStatus === 'REJECTED' ? '!' : '2'}
              </div>
              <span className="step-title">
                {pendingBooking.paymentStatus === 'REJECTED' ? 'مطلوب مراجعة' : 'تدقيق الإدارة'}
              </span>
              <span className="step-sub">جاري الفحص</span>
            </div>

            {/* Step 3 */}
            <div className="tracker-step">
              <div className="step-indicator-circle">3</div>
              <span className="step-title">إصدار التذكرة</span>
              <span className="step-sub">تأكيد المقعد</span>
            </div>
          </div>

          {/* Details & Action */}
          <div className="tracker-meta-card">
            <div className="tracker-meta-item">
              <strong>الجامعة المقصودة:</strong>
              <span>{pendingBooking.universityName}</span>
            </div>
            <div className="tracker-meta-item">
              <strong>تاريخ وموعد الرحلة:</strong>
              <span>{pendingBooking.tripDate} ({pendingBooking.departureTime || '06:30 ص'})</span>
            </div>
            <div className="tracker-meta-item">
              <strong>المقعد المحجوز:</strong>
              <span>مقعد #{pendingBooking.seatNumber}</span>
            </div>
            <div className="tracker-meta-item">
              <strong>المبلغ المطلوب:</strong>
              <span style={{ color: '#0284C7' }}>{pendingBooking.price} ج.م</span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 flex-wrap">
            {pendingBooking.paymentStatus === 'REJECTED' ? (
              <button
                className="btn-open-ticket-hero"
                style={{ maxWidth: '280px' }}
                onClick={() => navigate(`/student/payment/${pendingBooking.id}`)}
              >
                <CreditCard size={18} />
                <span>إعادة رفع إثبات التحويل</span>
                <ArrowLeft size={16} />
              </button>
            ) : (
              <button
                className="pass-sub-btn"
                onClick={() => navigate(`/student/booking/${pendingBooking.id}`)}
              >
                <Ticket size={16} />
                <span>متابعة تفاصيل الحجز</span>
                <ArrowLeft size={14} />
              </button>
            )}
          </div>
        </section>
      )}

      {/* ====================================================================
          3. Luxury Aviation-Grade Boarding Pass (If Confirmed Trip Exists)
          ==================================================================== */}
      {isLoading ? (
        <div className="skeleton-shimmer skeleton-ticket-card"></div>
      ) : upcomingBooking ? (
        <section className="luxury-boarding-pass">
          {/* Top Brand Bar */}
          <div className="pass-top-bar">
            <div className="pass-brand-label">
              <span className="pass-live-glow"></span>
              <div className="pass-title-group">
                <h3>تذكرة صعود معتمدة ومؤكدة</h3>
                <span>OFFICIAL VERIFIED BOARDING PASS • OTO STUDENTS</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="pass-code-pill"
                onClick={() => handleCopyBookingCode(upcomingBooking.bookingCode)}
                title="اضغط لنسخ كود الحجز"
                style={{ cursor: 'pointer', border: 'none' }}
              >
                {copiedCode === upcomingBooking.bookingCode ? (
                  <Check size={14} className="text-emerald-300" />
                ) : (
                  <Copy size={14} />
                )}
                <span>كود: {upcomingBooking.bookingCode}</span>
              </button>
            </div>
          </div>

          {/* Live Countdown Banner */}
          <div className="pass-countdown-strip">
            <div className="countdown-label">
              <Clock size={16} className="text-sky-600" />
              <span>
                {countdown.isPast
                  ? 'انطلقت الرحلة بالفعل — نتمنى لك سفراً ممتعاً 🚌'
                  : countdown.isToday
                  ? 'انطلاق الرحلة اليوم! يرجى التواجد قبل الموعد بـ 15 دقيقة:'
                  : 'العد التنازلي لموعد تحرك الحافلة:'}
              </span>
            </div>

            {!countdown.isPast && (
              <div className="countdown-clock">
                {countdown.days > 0 && (
                  <>
                    <span className="val">{countdown.days}</span>
                    <span>يوم :</span>
                  </>
                )}
                <span className="val">{String(countdown.hours).padStart(2, '0')}</span>
                <span>س :</span>
                <span className="val">{String(countdown.minutes).padStart(2, '0')}</span>
                <span>د :</span>
                <span className="val">{String(countdown.seconds).padStart(2, '0')}</span>
                <span>ث</span>
              </div>
            )}
          </div>

          {/* Flight / Journey Line Route Visualizer */}
          <div className="pass-journey-visual">
            <div className="journey-route-track">
              {/* Origin Point */}
              <div className="journey-point origin">
                <span className="journey-city-code">ALX</span>
                <span className="journey-city-name">الإسكندرية</span>
                <span className="text-xs text-slate-500">
                  {upcomingBooking.origin || 'سيدي جابر / محطة مصر'}
                </span>
              </div>

              {/* Animated Journey Line */}
              <div className="journey-connector">
                <div className="journey-line-dots"></div>
                <div className="journey-bus-avatar" title="حافلة سياحية مجهزة">
                  <Bus size={20} />
                </div>
                <span className="journey-est-duration">رحلة مباشرة وسريعة</span>
              </div>

              {/* Destination Point */}
              <div className="journey-point destination">
                <span className="journey-city-code">UNI</span>
                <span className="journey-city-name">{upcomingBooking.universityName}</span>
                <span className="text-xs text-slate-500">
                  {upcomingBooking.destination || 'الحرم الجامعي مباشرة'}
                </span>
              </div>
            </div>
          </div>

          {/* Perforated Cutout Separator */}
          <div className="pass-perforated-divider">
            <div className="pass-perforated-line"></div>
          </div>

          {/* Boarding Pass Specs Grid */}
          <div className="pass-specs-grid">
            <div className="pass-spec-item">
              <span className="label">تاريخ السفر:</span>
              <span className="val">
                <Calendar size={15} className="text-sky-600" />
                <span>{upcomingBooking.tripDate}</span>
              </span>
            </div>

            <div className="pass-spec-item">
              <span className="label">موعد التحرك:</span>
              <span className="val time-val">
                <Clock size={15} />
                <span>{upcomingBooking.departureTime || '06:30'} صباحاً</span>
              </span>
            </div>

            <div className="pass-spec-item">
              <span className="label">رقم المقعد المخصص:</span>
              <span className="val seat-val">
                <span className="seat-pill-badge">مقعد #{upcomingBooking.seatNumber}</span>
              </span>
            </div>

            <div className="pass-spec-item">
              <span className="label">نوع الحافلة:</span>
              <span className="val text-sm">
                <Bus size={14} className="text-slate-500" />
                <span>سياحية (50 راكباً) VIP</span>
              </span>
            </div>

            <div className="pass-spec-item">
              <span className="label">حالة السداد:</span>
              <span className="val text-emerald-600 font-extrabold text-sm flex items-center gap-1">
                <CheckCircle size={15} />
                <span>مدفوع ومعتمد ✅</span>
              </span>
            </div>
          </div>

          {/* Action Footer */}
          <div className="pass-action-footer">
            <button
              type="button"
              className="btn-open-ticket-hero"
              onClick={() => navigate(`/student/booking/${upcomingBooking.id}`)}
            >
              <Ticket size={20} />
              <span>استعراض التذكرة الرقمية ورمز QR للصعود</span>
              <ArrowLeft size={18} />
            </button>

            <div className="pass-secondary-actions">
              <button
                type="button"
                className="pass-sub-btn"
                onClick={() => handleShareTicketWithParent(upcomingBooking)}
                title="مشاركة تفاصيل التذكرة مع ولي الأمر عبر واتساب"
              >
                <Share2 size={16} />
                <span>مشاركة مع ولي الأمر</span>
              </button>

              <a
                href="https://maps.google.com/?q=Sidi+Gaber+Railway+Station+Alexandria"
                target="_blank"
                rel="noreferrer"
                className="pass-sub-btn"
                title="عرض موقع محطة التجمع على خرائط Google"
              >
                <MapPin size={16} />
                <span>موقع المحطة</span>
              </a>
            </div>
          </div>

          {shareSuccessNotice && (
            <div className="bg-emerald-50 text-emerald-800 text-xs font-bold px-4 py-2 border-t border-emerald-200 text-center">
              تم فتح واتساب وجاهز لمشاركة تفاصيل التذكرة بنجاح! 📲
            </div>
          )}
        </section>
      ) : (
        /* Empty Upcoming Trip Welcome Card */
        <section className="no-trip-welcome-card">
          <div className="no-trip-text-content">
            <div className="no-trip-icon-glow">
              <Compass size={32} />
            </div>
            <div className="no-trip-titles">
              <h3>هل تخطط لرحلتك القادمة؟ مقعدك في انتظارك!</h3>
              <p>
                احجز مقعدك في حافلات OTO السياحية (50 راكباً) المجهزة للجامعات. مقاعد مضمونة، انطلاق دقيق من الإسكندرية، وراحة تامة حتى باب كليتك.
              </p>
            </div>
          </div>

          <Link to="/student/universities" className="btn-book-first-trip">
            <PlusCircle size={18} />
            <span>احجز رحلتك القادمة الآن</span>
            <ArrowLeft size={16} />
          </Link>
        </section>
      )}

      {/* ====================================================================
          4. Vibrant Quick Action Tiles Hub
          ==================================================================== */}
      <section className="quick-actions-section">
        <div className="section-head-title-row">
          <h3>
            <Zap size={20} className="text-amber-500" />
            <span>خدمات الطلاب السريعة</span>
          </h3>
        </div>

        <div className="quick-actions-grid">
          {/* Tile 1: Book New Trip */}
          <Link to="/student/universities" className="action-tile-card featured-booking">
            <div className="action-tile-top">
              <div className="tile-icon-bubble red">
                <PlusCircle size={26} />
              </div>
              <span className="tile-badge-tag hot">مباشر ومضمون</span>
            </div>
            <div className="action-tile-body">
              <h4>حجز رحلة جديدة</h4>
              <p>اختر جامعتك، استعرض المواعيد المتاحة، وسيقوم النظام بتعيين مقعدك تلقائياً في الحافلة السياحية.</p>
            </div>
            <div className="action-tile-cta">
              <span>بدء الحجز الآن</span>
              <ArrowLeft size={16} />
            </div>
          </Link>

          {/* Tile 2: My Bookings & Tickets */}
          <Link to="/student/bookings" className="action-tile-card bookings-tile">
            <div className="action-tile-top">
              <div className="tile-icon-bubble blue">
                <History size={26} />
              </div>
              <span className="tile-badge-tag count">تذاكري السابقة والحالية</span>
            </div>
            <div className="action-tile-body">
              <h4>رحلاتي وتذاكري</h4>
              <p>استعرض حجوزاتك وحالات السداد، واحصل على تذكرتك الرقمية مع باركود QR للصعود في المحطة.</p>
            </div>
            <div className="action-tile-cta">
              <span>عرض التذاكر</span>
              <ArrowLeft size={16} />
            </div>
          </Link>

          {/* Tile 3: Demand Request (Unlisted / New Route) */}
          <div
            className="action-tile-card"
            onClick={() => setIsDemandModalOpen(true)}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
          >
            <div className="action-tile-top">
              <div className="tile-icon-bubble purple">
                <Send size={26} />
              </div>
              <span className="tile-badge-tag demand">خدمة الطلب المباشر</span>
            </div>
            <div className="action-tile-body">
              <h4>طلب خط أو موعد مخصص</h4>
              <p>جامعتك غير مدرجة أو تحتاج لموعد إضافي؟ سجل طلبك الآن ليتم توجيه حافلة (50 راكباً) لخط سيرك.</p>
            </div>
            <div className="action-tile-cta">
              <span style={{ color: '#9333EA' }}>تسجيل رغبة سفر</span>
              <ArrowLeft size={16} />
            </div>
          </div>

          {/* Tile 4: Student Profile */}
          <Link to="/student/profile" className="action-tile-card">
            <div className="action-tile-top">
              <div className="tile-icon-bubble amber">
                <User size={26} />
              </div>
              <span className="tile-badge-tag" style={{ background: '#FEF3C7', color: '#B45309' }}>
                بياناتك المسجلة
              </span>
            </div>
            <div className="action-tile-body">
              <h4>الملف الشخصي والبيانات</h4>
              <p>مراجعة وتحديث عنوان الإقامة بالإسكندرية، والجامعة المقيد بها، ورقم الهاتف للتواصل الميداني.</p>
            </div>
            <div className="action-tile-cta" style={{ color: '#B45309' }}>
              <span>إدارة الحساب</span>
              <ArrowLeft size={16} />
            </div>
          </Link>
        </div>
      </section>

      {/* ====================================================================
          5. Smart University & Route Explorer (Visual Hub)
          ==================================================================== */}
      <section className="uni-explorer-section">
        <div className="explorer-header-row">
          <div className="explorer-titles">
            <h3>مستكشف الجامعات ومواعيد السفر</h3>
            <p>اختر جامعتك أو محافظتك لاستعراض مواعيد الانطلاق المتاحة وحجز مقعدك فوراً</p>
          </div>

          <Link to="/student/universities" className="link-all-unis">
            <span>استعراض كل الجامعات ({universities.length})</span>
            <ArrowLeft size={16} />
          </Link>
        </div>

        {/* Controls: Search + Regional Segmented Tabs */}
        <div className="explorer-controls-wrapper">
          <div className="search-input-box">
            <Search size={18} className="icon-search" />
            <input
              type="text"
              placeholder="ابحث بالاسم (مثل: طنطا، كفر الشيخ، القاهرة، حلوان، بدر، المنصورة...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="btn-clear-input"
                onClick={() => setSearchQuery('')}
                title="مسح البحث"
              >
                ✕
              </button>
            )}
          </div>

          {/* Regional Tabs Pills */}
          <div className="regional-tabs-row">
            {REGIONAL_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                className={`region-tab-pill ${selectedRegion === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedRegion(cat.id)}
              >
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Popular Fast-Selection Chips */}
        {popularUnis.length > 0 && !searchQuery && (
          <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500 mt-1">
            <span className="font-bold text-slate-700">الأكثر طلباً:</span>
            {popularUnis.map((uni) => (
              <button
                key={uni.id}
                type="button"
                onClick={() => navigate(`/student/trips?universityId=${uni.id}`)}
                className="bg-slate-100 hover:bg-sky-100 hover:text-sky-700 text-slate-700 font-semibold px-3 py-1 rounded-full transition-colors flex items-center gap-1 cursor-pointer"
              >
                <Building2 size={12} />
                <span>{uni.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* University Pro Cards Grid */}
        {filteredUniversities.length > 0 ? (
          <div className="unis-pro-grid">
            {filteredUniversities.slice(0, 6).map((uni) => (
              <div
                key={uni.id}
                className="uni-pro-card"
                onClick={() => navigate(`/student/trips?universityId=${uni.id}`)}
                role="button"
                tabIndex={0}
              >
                <div>
                  <div className="uni-card-header">
                    <div className="uni-avatar-badge">
                      <Building2 size={24} />
                    </div>
                    <div className="uni-card-text-head">
                      <h4>{uni.name}</h4>
                      <span className="uni-loc-pill">
                        <MapPin size={13} className="text-sky-600" />
                        <span>{uni.governorate} {uni.campus ? `• ${uni.campus}` : ''}</span>
                      </span>
                    </div>
                  </div>

                  <div className="uni-card-badges-row mt-3">
                    <span className="uni-meta-chip">
                      <Clock size={12} />
                      <span>انطلاق يومي 06:30 ص</span>
                    </span>
                    <span className="uni-meta-chip price-chip">
                      <span>160 ج.م / مقعد</span>
                    </span>
                  </div>
                </div>

                <button type="button" className="uni-card-btn-action">
                  <span>استعراض المواعيد والمقاعد</span>
                  <ArrowLeft size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center">
            <Building2 size={36} className="text-slate-400 mx-auto mb-2" />
            <h4 className="font-bold text-slate-800 text-base mb-1">
              لم نعثر على جامعة تطابق البحث "{searchQuery}"
            </h4>
            <p className="text-slate-500 text-sm max-w-md mx-auto mb-4">
              جامعتك غير مدرجة بعد أو لم تجد الموعد المطلوب؟ يمكنك تسجيل رغبتك الآن وسنقوم بدراسة فتح خط حافلات فوراً.
            </p>
            <button
              type="button"
              onClick={() => setIsDemandModalOpen(true)}
              className="btn-primary"
              style={{ display: 'inline-flex', padding: '0.65rem 1.25rem', fontSize: '0.9rem' }}
            >
              <Send size={16} />
              <span>تسجيل طلب خط لجامعتي</span>
            </button>
          </div>
        )}
      </section>

      {/* Demand Feedback Notice Banner */}
      {demandSuccessNotice && (
        <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 p-4 rounded-xl flex items-center gap-3">
          <CheckCircle size={22} className="text-emerald-600 flex-shrink-0" />
          <span className="font-bold text-sm">{demandSuccessNotice}</span>
        </div>
      )}

      {/* ====================================================================
          6. Fleet Comfort, Safety & Student Peace of Mind Showcase
          ==================================================================== */}
      <section className="fleet-peace-of-mind-card">
        <div className="fleet-trust-head">
          <div className="fleet-title-wrapper">
            <Bus size={26} className="text-sky-400" />
            <h3>معايير أمان وراحة أسطول OTO STUDENTS المعتمد</h3>
          </div>
          <span className="fleet-badge-gold">حافلات سياحية VIP (50 راكباً)</span>
        </div>

        <div className="fleet-features-grid">
          <div className="fleet-feature-item">
            <div className="feature-icon-box">
              <Bus size={22} />
            </div>
            <div className="feature-text-block">
              <h5>حافلات سياحية حديثة 50 راكباً</h5>
              <p>مكيفة بالكامل، مقاعد مريحة مخصصة لكل طالب بدون تزاحم.</p>
            </div>
          </div>

          <div className="fleet-feature-item">
            <div className="feature-icon-box">
              <Compass size={22} />
            </div>
            <div className="feature-text-block">
              <h5>تتبع حي ومباشر بالـ GPS</h5>
              <p>متابعة لحظية لمسار الحافلة من لحظة التحرك وحتى الوصول للجامعة.</p>
            </div>
          </div>

          <div className="fleet-feature-item">
            <div className="feature-icon-box">
              <Zap size={22} />
            </div>
            <div className="feature-text-block">
              <h5>شواحن USB ومقاعد مريحة</h5>
              <p>مجهزة لراحة الطلاب لشحن الأجهزة والاتصال طوال مسار السفر.</p>
            </div>
          </div>

          <div className="fleet-feature-item">
            <div className="feature-icon-box">
              <Phone size={22} />
            </div>
            <div className="feature-text-block">
              <h5>إشراف ميداني ودعم 24/7</h5>
              <p>مشرف ميداني بكل محطة وخط ساخن مستمر لمساعدة أولياء الأمور والطلاب.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demand Data Entry Modal for Custom University Request */}
      <DemandDataEntryModal
        isOpen={isDemandModalOpen}
        onClose={() => setIsDemandModalOpen(false)}
        initialStudentName={studentProfile?.fullName}
        initialPhone={studentProfile?.phoneNumber}
        initialUniversityId={studentProfile?.universityId}
        initialUniversityName={studentProfile?.universityName || searchQuery}
        onRecordCreated={(newRec) => {
          setDemandSuccessNotice(
            `تم تسجيل طلبك لجامعة (${newRec.universityNameSnapshot}) بنجاح! فريق العمليات يدرس توجيه حافلة لمسارك.`
          );
          setTimeout(() => setDemandSuccessNotice(null), 8000);
        }}
      />
    </div>
  );
};

export default StudentDashboard;
